/**
 * Stecknadel auf einem Plan (Betreiber 28.09.2026: „Nadeln statt Punkte“,
 * dann „wie eine echte Pinnnadel … wenn mehrere Nadeln übereinander sind,
 * drehe sie leicht weg“).
 *
 * Glaenzende Kugel auf einem duennen Metallstift, leicht schraeg gesteckt.
 * Die SPITZE steht exakt auf der Koordinate (x/y in Plan-Prozent), die Kugel
 * am Ende des Stifts: Kugelmitte = Spitze + L·(sin θ, −cos θ) mit
 * L = NADEL_STIFT_FAKTOR · R und θ = `neigung` (0 = senkrecht, + = Kugel nach
 * rechts). Licht von oben links: Glanzlicht oben links auf der Kugel, weicher
 * Schatten rechts unten auf dem Papier.
 *
 * Das Elternelement muss `position: relative` sein, genau die Groesse des
 * Planbilds haben und `isolation: isolate` tragen: die Nadeln stapeln sich
 * ueber `zIndex` (tiefer stehende Nadel liegt vorn, Schatten ganz unten),
 * diese Werte duerfen nicht aus der Planflaeche herausreichen.
 * Neigungen fuer nahe beieinander stehende Nadeln liefert
 * services/planNadelNeigung (`nadelNeigungen`, Abstand `nadelAbstandPx`).
 */
// Masse, Geometrie und Farbtoene gehoeren zur Nadel und werden mit ihr
// exportiert (Gruppenabstand fuer Dialog/Sicherheitsplan, Tests) — Fast Refresh
// laedt diese Datei dann eben ganz neu.
/* eslint-disable react-refresh/only-export-components */
import React, { useId } from 'react';
import type { JSX } from 'react';
import { NADEL_GRUNDNEIGUNG, NADEL_MAX_NEIGUNG } from '../../services/planNadelNeigung';

export type NadelGroesse = 'normal' | 'klein';

export interface PlanNadelProps {
    /** Plan-Prozent 0–100. */
    x: number;
    y: number;
    /** Text in der Kugel (Mangelnummer, Punktnummer); leer = nur Kugel. */
    nummer?: string | number | null;
    /** Schild ueber der Kugel (z. B. „Erste Hilfe“). */
    beschriftung?: string;
    /** Farbe der Kugel als Hex (#rgb/#rrggbb); anderes = flache Farbe. Standard Rot (Mangel). */
    farbe?: string;
    groesse?: NadelGroesse;
    /** Nadel eines anderen Eintrags: blass, nicht antippbar. */
    blass?: boolean;
    /**
     * Zoom der Planansicht, wenn sie per CSS-`transform: scale` vergroessert:
     * die Nadel wird dagegen skaliert und bleibt gleich gross. Bei Zoom ueber
     * die Breite weglassen.
     */
    gegenSkala?: number;
    onClick?: (e: React.MouseEvent) => void;
    title?: string;
    /** Kleines Loeschkreuz an der Kugel (Hinweis „antippen = entfernen“). */
    entfernbar?: boolean;
    /** Neigung in Grad (0 = senkrecht, + = Kugel nach rechts); Standard NADEL_GRUNDNEIGUNG. */
    neigung?: number;
}

// --- Masse (px) ---------------------------------------------------------------

/** Kugelradius R (px) je Groesse. */
export const NADEL_KUGEL_PX: Readonly<Record<NadelGroesse, number>> = { normal: 11, klein: 8 };
/** Stiftlaenge L (Spitze → Kugelmitte) in Kugelradien. */
export const NADEL_STIFT_FAKTOR = 2.3;
/** Spitzenabstand in Kugelradien, unter dem Nadeln als Gruppe aufgefaechert werden. */
export const NADEL_GRUPPEN_FAKTOR = 2.2;
/** Staerke des Metallstifts (px) je Groesse. */
export const NADEL_STIFT_PX: Readonly<Record<NadelGroesse, number>> = { normal: 2, klein: 1.6 };
/** Versatz des Kugelschattens gegen die Kugelmitte in Kugelradien (Licht oben links). */
export const NADEL_SCHATTEN_VERSATZ = { x: 0.75, y: 0.9 } as const;

/** Abstand (px) fuer `nadelNeigungen`: naeher stehende Spitzen faechern auf. */
export const nadelAbstandPx = (groesse: NadelGroesse = 'normal'): number =>
    NADEL_GRUPPEN_FAKTOR * NADEL_KUGEL_PX[groesse];

export interface Punkt { x: number; y: number }

export interface StecknadelGeometrie {
    /** Tatsaechliche Neigung (Grad), auf ±NADEL_MAX_NEIGUNG begrenzt. */
    neigung: number;
    /** Kugelradius (px). */
    r: number;
    /** Kugelmitte relativ zur Spitze (px, y nach unten). */
    kugel: Punkt;
    /** Mitte des Kugelschattens auf dem Papier relativ zur Spitze (px). */
    schatten: Punkt;
    /** Einheitsvektor Spitze → Kugel. */
    richtung: Punkt;
    /** Einheitsvektor quer zum Stift (rechte Seite, vom Licht abgewandt). */
    quer: Punkt;
}

const runden = (wert: number): number => Math.round(wert * 100) / 100;

/** Lage von Kugel und Schatten relativ zur Spitze (px). */
export const stecknadelGeometrie = (neigung?: number, groesse: NadelGroesse = 'normal'): StecknadelGeometrie => {
    const r = NADEL_KUGEL_PX[groesse];
    const grad = typeof neigung === 'number' && Number.isFinite(neigung)
        ? Math.max(-NADEL_MAX_NEIGUNG, Math.min(NADEL_MAX_NEIGUNG, neigung))
        : NADEL_GRUNDNEIGUNG;
    const bogen = (grad * Math.PI) / 180;
    const sin = Math.sin(bogen);
    const cos = Math.cos(bogen);
    const l = NADEL_STIFT_FAKTOR * r;
    const kugel = { x: runden(l * sin), y: runden(-l * cos) };
    return {
        neigung: grad,
        r,
        kugel,
        schatten: {
            x: runden(kugel.x + NADEL_SCHATTEN_VERSATZ.x * r),
            y: runden(kugel.y + NADEL_SCHATTEN_VERSATZ.y * r),
        },
        richtung: { x: sin, y: -cos },
        quer: { x: cos, y: sin },
    };
};

/**
 * Stapelhoehe: tiefer stehende Nadel (groesseres y) liegt vorn, blasse Nadeln
 * immer unter den aktiven. Schatten liegen mit 0 unter allen Nadeln.
 */
export const nadelZIndex = (y: number, blass = false): number => {
    const stufe = Math.round(Math.min(100, Math.max(0, Number.isFinite(y) ? y : 0)) * 10);
    return (blass ? 1 : 1010) + stufe;
};

// --- Farben --------------------------------------------------------------------

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const hexZuRgb = (farbe: string): [number, number, number] | null => {
    const treffer = HEX.exec(farbe.trim());
    if (!treffer) return null;
    const ziffern = treffer[1].length === 3
        ? treffer[1].split('').map(z => z + z).join('')
        : treffer[1];
    return [0, 2, 4].map(i => parseInt(ziffern.slice(i, i + 2), 16)) as [number, number, number];
};

/**
 * Hexfarbe mit Weiss (anteil > 0) oder Schwarz (anteil < 0) mischen,
 * |anteil| 0–1. Keine Hexfarbe → null.
 */
export const farbeMischen = (farbe: string, anteil: number): string | null => {
    const rgb = hexZuRgb(farbe);
    if (!rgb) return null;
    const a = Math.max(-1, Math.min(1, anteil));
    const ziel = a >= 0 ? 255 : 0;
    const betrag = Math.abs(a);
    return `#${rgb
        .map(k => Math.round(k + (ziel - k) * betrag).toString(16).padStart(2, '0'))
        .join('')}`;
};

export interface NadelFarbtoene {
    glanz: string;
    hell: string;
    grund: string;
    dunkel: string;
    rand: string;
}

/** Toene der Kugel aus einer Hexfarbe; anderes → null (flache Farbe). */
export const nadelFarbtoene = (farbe: string): NadelFarbtoene | null => {
    const grund = hexZuRgb(farbe) ? farbe.trim().toLowerCase() : null;
    if (!grund) return null;
    return {
        glanz: farbeMischen(grund, 0.6) as string,
        hell: farbeMischen(grund, 0.22) as string,
        grund,
        dunkel: farbeMischen(grund, -0.42) as string,
        rand: farbeMischen(grund, -0.62) as string,
    };
};

// --- Nadel ---------------------------------------------------------------------

/** Schriftgroesse der Nummer (px): ab 3 Zeichen kleiner. */
const schriftGroesse = (text: string, groesse: NadelGroesse): number => {
    const basis = groesse === 'klein' ? 8.5 : 11;
    if (text.length <= 2) return basis;
    return text.length === 3 ? basis - 2 : basis - 3;
};

export function PlanNadel({
    x, y, nummer, beschriftung, farbe = '#dc2626', groesse = 'normal', blass = false,
    gegenSkala, onClick, title, entfernbar = false, neigung,
}: PlanNadelProps): JSX.Element {
    const id = `nadel${useId().replace(/[^A-Za-z0-9_-]/g, '')}`;
    const g = stecknadelGeometrie(neigung, groesse);
    const { r, kugel: k, schatten: s, richtung: d, quer: q } = g;
    const toene = nadelFarbtoene(farbe);
    const text = nummer === null || nummer === undefined ? '' : String(nummer);
    const skala = gegenSkala && gegenSkala > 0 ? 1 / gegenSkala : 1;
    const klickbar = !!onClick && !blass;

    // Zeichenflaeche um die Spitze; ueberstehendes (Schatten, Blur) bleibt sichtbar.
    const e = Math.ceil(NADEL_STIFT_FAKTOR * r + 2.6 * r);
    const svgRahmen: React.CSSProperties = { position: 'absolute', left: -e, top: -e, overflow: 'visible' };
    const svgAttr = { width: 2 * e, height: 2 * e, viewBox: `${-e} ${-e} ${2 * e} ${2 * e}` };
    const lage: React.CSSProperties = {
        left: `${x}%`,
        top: `${y}%`,
        width: 0,
        height: 0,
        transform: skala !== 1 ? `scale(${skala})` : undefined,
        transformOrigin: '0 0',
    };

    // Metallstift: schmale Spitze, dann volle Staerke bis unter die Kugel.
    const w = NADEL_STIFT_PX[groesse] / 2;
    const spitze = 0.45 * r;
    const punkt = (p: Punkt): string => `${runden(p.x)},${runden(p.y)}`;
    const stift = [
        { x: 0, y: 0 },
        { x: d.x * spitze + q.x * w, y: d.y * spitze + q.y * w },
        { x: k.x + q.x * w, y: k.y + q.y * w },
        { x: k.x - q.x * w, y: k.y - q.y * w },
        { x: d.x * spitze - q.x * w, y: d.y * spitze - q.y * w },
    ].map(punkt).join(' ');
    const mitte = { x: k.x / 2, y: k.y / 2 };

    // Glanzlicht oben links: weicher Schein + scharfer Lichtpunkt.
    const glanz = { x: runden(k.x - 0.36 * r), y: runden(k.y - 0.4 * r) };
    const lichtpunkt = { x: runden(k.x - 0.42 * r), y: runden(k.y - 0.47 * r) };
    // Reflexlicht vom Papier: schwacher heller Bogen innen am Rand unten rechts.
    const bogenR = 0.8 * r;
    const aufBogen = (grad: number): string => {
        const b = (grad * Math.PI) / 180;
        return `${runden(k.x + bogenR * Math.cos(b))} ${runden(k.y + bogenR * Math.sin(b))}`;
    };
    const reflex = `M ${aufBogen(10)} A ${runden(bogenR)} ${runden(bogenR)} 0 0 1 ${aufBogen(100)}`;
    const konturText = toene ? toene.rand : 'rgba(0,0,0,0.55)';
    const schrift = schriftGroesse(text, groesse);
    const schildAbstand = entfernbar && klickbar ? 6 : 3;

    return (
        <>
            {/* Schatten + Einstich auf dem Papier: unter allen Nadeln. */}
            <div
                className="pointer-events-none absolute"
                style={{ ...lage, zIndex: 0, opacity: blass ? 0.5 : undefined }}
                aria-hidden="true"
            >
                <svg {...svgAttr} style={svgRahmen} aria-hidden="true">
                    <defs>
                        <filter id={`${id}-weich`} x="-100%" y="-100%" width="300%" height="300%">
                            <feGaussianBlur stdDeviation={runden(0.3 * r)} />
                        </filter>
                        <filter id={`${id}-fein`} x="-100%" y="-100%" width="300%" height="300%">
                            <feGaussianBlur stdDeviation={runden(0.08 * r)} />
                        </filter>
                    </defs>
                    <line
                        x1={0} y1={0} x2={s.x} y2={s.y}
                        stroke="#000000" strokeOpacity={0.2} strokeWidth={NADEL_STIFT_PX[groesse]}
                        strokeLinecap="round" filter={`url(#${id}-fein)`}
                    />
                    <ellipse
                        cx={s.x} cy={s.y} rx={runden(1.05 * r)} ry={runden(0.85 * r)}
                        transform={`rotate(40 ${s.x} ${s.y})`}
                        fill="#000000" fillOpacity={0.26} filter={`url(#${id}-weich)`}
                    />
                    <circle cx={0} cy={0} r={groesse === 'klein' ? 1 : 1.3} fill="#0f172a" fillOpacity={0.6} />
                </svg>
            </div>

            <div
                className={`pointer-events-none absolute ${klickbar ? 'group cursor-pointer' : ''} ${blass ? 'opacity-50' : ''}`}
                style={{ ...lage, zIndex: nadelZIndex(y, blass) }}
                data-neigung={runden(g.neigung)}
                onClick={klickbar ? (ev) => { ev.stopPropagation(); onClick?.(ev); } : undefined}
                title={title}
                role={klickbar ? 'button' : undefined}
                aria-label={title}
            >
                <svg {...svgAttr} style={svgRahmen} aria-hidden="true">
                    <defs>
                        <linearGradient
                            id={`${id}-stift`}
                            gradientUnits="userSpaceOnUse"
                            x1={runden(mitte.x - q.x * w)} y1={runden(mitte.y - q.y * w)}
                            x2={runden(mitte.x + q.x * w)} y2={runden(mitte.y + q.y * w)}
                        >
                            <stop offset="0" stopColor="#f8fafc" />
                            <stop offset="0.4" stopColor="#cbd5e1" />
                            <stop offset="0.75" stopColor="#64748b" />
                            <stop offset="1" stopColor="#334155" />
                        </linearGradient>
                        {toene && (
                            <radialGradient id={`${id}-kugel`} cx="0.4" cy="0.36" r="0.68" fx="0.3" fy="0.26">
                                <stop offset="0" stopColor={toene.glanz} />
                                <stop offset="0.3" stopColor={toene.hell} />
                                <stop offset="0.62" stopColor={toene.grund} />
                                <stop offset="0.9" stopColor={toene.dunkel} />
                                <stop offset="1" stopColor={toene.rand} />
                            </radialGradient>
                        )}
                        <filter id={`${id}-glanz`} x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation={runden(0.1 * r)} />
                        </filter>
                    </defs>
                    <polygon points={stift} fill={`url(#${id}-stift)`} stroke="#475569" strokeWidth={0.3} strokeLinejoin="round" />
                    <g
                        className={klickbar ? 'transition-transform duration-150 group-hover:scale-[1.15]' : undefined}
                        style={{ transformBox: 'fill-box', transformOrigin: 'center', pointerEvents: klickbar ? 'auto' : 'none' }}
                    >
                        <circle
                            cx={k.x} cy={k.y} r={r}
                            fill={toene ? `url(#${id}-kugel)` : farbe}
                            stroke={toene ? toene.rand : 'rgba(0,0,0,0.45)'}
                            strokeWidth={0.8}
                        />
                        <path
                            d={reflex} fill="none" stroke={toene ? toene.glanz : '#ffffff'}
                            strokeOpacity={0.45} strokeWidth={runden(0.14 * r)} strokeLinecap="round"
                            filter={`url(#${id}-glanz)`}
                        />
                        <ellipse
                            cx={glanz.x} cy={glanz.y} rx={runden(0.4 * r)} ry={runden(0.26 * r)}
                            transform={`rotate(-40 ${glanz.x} ${glanz.y})`}
                            fill="#ffffff" fillOpacity={0.5} filter={`url(#${id}-glanz)`}
                        />
                        <ellipse
                            cx={lichtpunkt.x} cy={lichtpunkt.y} rx={runden(0.18 * r)} ry={runden(0.11 * r)}
                            transform={`rotate(-40 ${lichtpunkt.x} ${lichtpunkt.y})`}
                            fill="#ffffff" fillOpacity={0.92}
                        />
                        {text && (
                            <text
                                x={k.x}
                                y={runden(k.y + 0.5)}
                                textAnchor="middle"
                                dominantBaseline="central"
                                fontSize={schrift}
                                fontWeight={700}
                                fill="#ffffff"
                                stroke={konturText}
                                strokeWidth={1.7}
                                strokeLinejoin="round"
                                paintOrder="stroke"
                                fontFamily="system-ui, sans-serif"
                            >
                                {text}
                            </text>
                        )}
                    </g>
                </svg>
                {beschriftung && (
                    <span
                        className={`absolute max-w-[10rem] truncate whitespace-nowrap rounded bg-slate-900/90 px-1.5 py-0.5 text-[11px] font-bold leading-tight text-white shadow ${klickbar ? 'pointer-events-auto' : ''}`}
                        style={{ left: k.x, top: runden(k.y - r - schildAbstand), transform: 'translate(-50%, -100%)' }}
                    >
                        {beschriftung}
                    </span>
                )}
                {entfernbar && klickbar && (
                    <span
                        className="pointer-events-auto absolute flex h-3.5 w-3.5 items-center justify-center rounded-full border border-slate-200 bg-white text-[10px] font-bold leading-none text-red-600 shadow md:opacity-0 md:group-hover:opacity-100"
                        style={{ left: runden(k.x + 0.72 * r), top: runden(k.y - 0.72 * r), transform: 'translate(-50%, -50%)' }}
                    >
                        ×
                    </span>
                )}
            </div>
        </>
    );
}
